import {
  createApp, h, ref, watch,
} from 'vue'
import type { VNodeProps, Slot, App } from 'vue'
import { merge } from 'lodash'
import Modal from './Modal.vue'
import BeerPreviewModal from './PreviewModal.vue'
import BeerQRPreviewModal from './QRPreviewModal.vue'
import { isBrowser } from '../../../utils/env'

type RawProps = VNodeProps & Record<string, unknown>

type RawSlots = {
  [name: string]: Slot | undefined
}
type GetSlotType = (() => RawSlots) | null

type OptionType = {
  getScopedSlots?: GetSlotType
  props?: RawProps
  ModalCmp?: unknown
}

export function showModal(
  {
    getScopedSlots = null,
    props = {},
    ModalCmp = Modal,
  }: OptionType,
): () => void {
  if (!isBrowser) {
    // eslint-disable-next-line
    return () => {}
  }
  let container: HTMLElement | null = document.createElement('div')
  document.body.appendChild(container)

  let app: App<Element> | null = null

  function destroy() {
    if (container) {
      app?.unmount()
      document.body.removeChild(container)
      container = null
      app = null
    }
  }
  const show = ref(true)

  app = createApp({
    setup() {
      const onShowUpdate = (val: boolean) => {
        show.value = val
      }
      watch(show, val => {
        if (!val) {
          setTimeout(() => {
            destroy()
          }, 240)
        }
      }, {
        flush: 'post',
      })

      return () => h(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ModalCmp as any,
        {
          ...props,
          show: show.value,
          'onUpdate:show': onShowUpdate,
        },
        getScopedSlots?.(),
      )
    },
  })
  if (app) {
    app.mount(container)
  }
  return () => {
    show.value = false
  }
}

export function withModal(props: RawProps, getScopedSlots: GetSlotType) {
  return (options: OptionType = {}): () => void => showModal(
    merge(
      {},
      {
        getScopedSlots,
        props,
      },
      options,
    ),
  )
}

export function withDialog(props: RawProps = {}) {
  return (onConfirm: () => void): () => void => {
    const { handleConfirm, ...restProps } = props
    return showModal({
      props: {
        type: 'dialog',
        handleConfirm: (
          { close } : { close: () => void },
        ) => {
          onConfirm?.()
          // @ts-ignore
          handleConfirm?.()
          close()
        },
        ...restProps,
      },
    })
  }
}

export function showPreview(props: RawProps, getScopedSlots: GetSlotType) : () => void {
  return showModal({
    getScopedSlots,
    props,
    ModalCmp: BeerPreviewModal,
  })
}

export function showQrPreview(props: RawProps, getScopedSlots: GetSlotType): () => void {
  return showModal({
    getScopedSlots,
    props,
    ModalCmp: BeerQRPreviewModal,
  })
}
